import {
  Box,
  Button,
  Flex,
  forwardRef,
  Grid,
  Heading,
  Icon,
  Image,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import type { InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { formatPersonAddress, formatPersonFullName } from 'BrokerTsLib/src/dtos'
import { useEffect, useState } from 'react'

import { useOffersVehicleContext } from '@/context/OffersVehicleProvider'
import type { IPrintContext } from '@/context/PrintContext'
import { usePrintContext } from '@/context/PrintContext'
import { useGetBrandLogo } from '@/hooks/useDocuments'
import { Car } from '@/icons'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { useInsuranceParams } from '../context/InsuranceParamsProvider'
import { useInsuranceInfo } from '../hooks/useInsuranceInfo'
import { useInvokeBmw } from '../hooks/useOffer'
import { InsuranceInfo } from './InsuranceInfo'
import { Container } from './ui/Container'

interface HeaderProps {
  logo?: string
  brand?: string | null
  model?: string | null
  onHeightChange?: (height: number, isScrolled: boolean) => void
  onOpenDriverInfo?: () => void
}

export const LeasingHeader = forwardRef((props: HeaderProps, ref) => {
  const { translate, translateFormat } = useTranslationsContext()
  const { setPrintMode, setType } = usePrintContext()
  const { insuranceParams: ip, insuranceParamsId } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsLeasingDto
  const [isGoingBack, setIsGoingBack] = useState(false)
  const { offers, updateOffer } = useOffersVehicleContext()

  const { brand, model, onHeightChange, onOpenDriverInfo } = props
  const [isScrolled, setIsScrolled] = useState(false)
  const [logoSize, setLogoSize] = useState(64)
  const insuranceInfo = useInsuranceInfo(insuranceParams)
  const { mutateAsync: invokeBmw } = useInvokeBmw()

  const [showPrintButton, setShowPrintButton] = useState((insuranceParams?.generalParams.lastWizardStep ?? 0) > 1)

  const druh = insuranceParams?.vehicle.druh
  const { data: logoData, isLoading: isLogoLoading, isError: isLogoError } = useGetBrandLogo(druh, brand, { retry: false, enabled: !!druh && !!brand })

  // function to check if window is scrolled down

  useEffect(() => {
    if (typeof window === 'undefined') return
    // const margin = 32

    const handleScroll = () => {
      if (typeof window === 'undefined') return

      if (window.scrollY > 0) {
        setIsScrolled(true)
        onHeightChange?.(122, isScrolled)
      } else {
        setIsScrolled(false)
        onHeightChange?.(148, isScrolled)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [onHeightChange, isScrolled])

  useEffect(() => {
    setLogoSize(isScrolled ? 30 : 64)
  }, [isScrolled])

  useEffect(() => {
    setShowPrintButton((insuranceParams?.generalParams.lastWizardStep ?? 0) > 1)
  }, [insuranceParams?.generalParams.lastWizardStep])

  const handleSetPrintMode = (printMode: boolean, type?: IPrintContext['type']) => {
    setPrintMode(printMode)
    setType(type ?? null)

    offers.forEach(o => {
      o.print = printMode && o.price > 0 && o.included && o.errors.filter(e => !e.softError).length == 0
      updateOffer(o)
    })
  }

  return (
    <Box
      ref={ref}
      as="header"
      sx={{
        position: { base: 'static', md: 'fixed' },
        top: 0,
        left: 0,
        right: 0,
        mt: { md: isScrolled ? 0 : 8 },
        py: 4,
        bg: 'primary.800',
        transition: 'all 200ms ease-in-out',
        zIndex: 'sticky',
      }}
    >
      <Container>
        <Grid
          sx={{
            gridTemplateColumns: { base: '1fr', md: 'auto 1fr' },
            gap: { base: 4, md: 8 },
          }}
        >
          <Grid
            sx={{
              w: { base: '100%', md: '250px' },
              gridTemplateColumns: {
                base: 'auto 1fr',
                md: '1fr',
              },
              mt: { md: isScrolled ? 0 : -8 },
              mb: { md: -4 },
              borderRadius: { base: 'md', md: 0 },
              overflow: {
                base: 'hidden',
                md: 'visible',
              },
              transition: 'all 200ms ease-in-out',
            }}
          >
            <Grid
              sx={{
                p: { base: 2, md: isScrolled ? 4 : 7 },
                justifyContent: 'center',
                bg: 'white',
                borderTopLeftRadius: { md: 'md' },
                borderTopRightRadius: { md: 'md' },
                transition: 'all 200ms ease-in-out',
              }}
            >
              {isLogoLoading ?
                <Skeleton sx={{
                  width: {
                    base: 12,
                    md: `${logoSize}px`,
                  },
                  height: {
                    base: 12,
                    md: `${logoSize}px`,
                  },
                  transition: 'all 200ms ease-in-out',
                }} />
                // A fallback when error occurs
                : isLogoError ?
                  <Icon boxSize={{
                    base: 12,
                    md: `${logoSize}px`,
                  }} as={Car} />
                  : logoData &&
                  <Image
                    src={'data:image/png;base64,' + logoData.data}
                    alt={brand ?? undefined}
                    sx={{
                      width: {
                        base: 12,
                        md: `${logoSize}px`,
                      },
                      height: {
                        base: 12,
                        md: `${logoSize}px`,
                      },
                      transition: 'all 200ms ease-in-out',
                    }}
                  />
              }
            </Grid>

            <Grid
              sx={{
                px: 4,
                pt: { base: 0, md: 2 },
                alignContent: 'center',
                alignItems: 'center',
                bg: 'neutral.50',
              }}
            >
              {brand && <Text sx={{ fontSize: 'sm' }}>{brand}</Text>}
              {model && (
                <Text sx={{ fontSize: 'sm', fontWeight: 600 }}>{model}</Text>
              )}
            </Grid>
          </Grid>

          <Grid
            sx={{
              px: { md: 4 },
              gap: { base: 4, md: isScrolled ? 4 : 8 },
              alignContent: 'start',
              alignItems: 'start',
              transition: 'all 200ms ease-in-out',
            }}
          >
            <Heading
              sx={{
                color: 'white',
                fontSize: { base: 'md', md: isScrolled ? 'lg' : '3xl' },
                transition: 'all 200ms ease-in-out',
              }}
            >
              {translateFormat('step2.wizardHeader.title._paramsId', `Nabídka pojištění č. ${insuranceParams?.generalParams.paramsId}, `, { paramsId: insuranceParams?.generalParams.paramsId })}
              <strong>
                {formatPersonFullName(insuranceParams?.client)}, {formatPersonAddress(insuranceParams?.client?.address)}
              </strong>
            </Heading>

            <Grid
              sx={{
                gap: { base: 4, md: isScrolled ? 4 : 8 },
                alignItems: 'center',
                gridTemplateColumns: {
                  base: '1fr',
                  md: '1fr auto',
                },
                justifyContent: 'space-between',
                transition: 'all 200ms ease-in-out',
              }}
            >
              <Flex
                sx={{
                  gap: { base: 4, md: 8 },
                  flexWrap: 'wrap',
                  transition: 'all 200ms ease-in-out',
                }}
              >
                {insuranceInfo &&
                  insuranceInfo.map((info) => (
                    <InsuranceInfo
                      key={info.label}
                      label={info.label}
                      value={info.value}
                      isScrolled={isScrolled}
                      hasWarning={info.hasWarning}
                    />
                  ))}
              </Flex>

              <Flex sx={{ gap: 2 }}>
                {onOpenDriverInfo &&
                  <Button onClick={onOpenDriverInfo}>
                    {translate('leasingSimpleWizard.driverInfoDialog.title', 'Doplňující údaje')}
                  </Button>
                }
                {showPrintButton && !!setPrintMode &&
                  ((offers.filter(x => x.price > 0).length ?? 0) > 0
                    ? <Button onClick={() => { handleSetPrintMode(true, 'print') }}>{translate('print', 'Tisk')}</Button>
                    : <Button isDisabled>{translate('print', 'Tisk')}</Button>
                  )
                }
                <Button colorScheme='red' type='submit'
                  isLoading={isGoingBack}
                  onClick={() => {
                    setIsGoingBack(true)
                    invokeBmw({ action: 'Cancel', paramsId: insuranceParamsId! }).catch(() => {
                      alert(translate('errorWhenProcessingRequest', 'Při zpracování požadavku došlo k chybě'))
                      setIsGoingBack(false)
                    })
                  }}>
                  {translate('back', 'Zpět')}
                </Button>
              </Flex>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box >
  )
})
